import React, { useEffect, useState } from "react";
import { MdArrowBack, MdCancel, MdCheckCircle, MdPlayArrow, MdSave, MdSchedule, MdPersonOutline, MdGroup, MdSend } from "react-icons/md";
import { TfiTicket } from "react-icons/tfi";
import Select from "react-select";
import { getTicketMensagens, updateTicket, updateTicketStatus } from "../services/tickets";
import { getTomadoresContact, getUtilizadoresContact } from "../services/Contacts";
import { styles } from "../styles/TicketDetails.styles";

const getTicketId = (ticket) => ticket.STAMP || ticket.PUBLIC_ID || ticket.id;
const getTicketRef = (ticket) => ticket.TICKET_REF;
const getStatus = (ticket) => {
  const status = String(ticket.ESTADO || ticket.STATUS || ticket.status || "aberto").trim().toLowerCase();
  return status === "pedente" ? "pendente" : status;
};
const getObservation = (ticket) => ticket.OBSERVACAO || ticket.observacao || ticket.OBSERVACOES || ticket.observacoes || "";
const hasValue = (...values) => values.some((value) => value !== undefined && value !== null && String(value).trim() !== "");
const hasManager = (ticket) => hasValue(ticket.GESTOR_STAMP, ticket.GESTOR_ID, ticket.GESTOR_NOME, ticket.GESTOR, ticket.gestor, ticket.RESPONSAVEL, ticket.responsavel);
const hasTomador = (ticket) => hasValue(ticket.TOMADOR_STAMP, ticket.TOMADOR_ID, ticket.TOMADOR_NOME, ticket.TOMADOR, ticket.tomador);
const getTicketSenderRole = (value) => {
  const normalized = String(value || "").trim().toLowerCase();

  if (["tomador", "cliente", "user", "utilizador"].some((entry) => normalized.includes(entry))) {
    return "user";
  }

  if (["suporte", "support", "admin", "staff", "atendente", "operador"].some((entry) => normalized.includes(entry))) {
    return "support";
  }

  return "user";
};
const renderSupportMessage = (text) => {
  const parts = String(text).split(/(https?:\/\/[^\s]+)/gi);

  return parts.map((part, index) => {
    if (!/^https?:\/\//i.test(part)) return part;

    const trailingPunctuation = part.match(/[.,!?;:)\]]+$/)?.[0] || "";
    const imageUrl = part.slice(0, part.length - trailingPunctuation.length);
    let isImageUrl = false;

    try {
      isImageUrl = /\.(apng|avif|bmp|gif|jpe?g|png|svg|webp)$/i.test(new URL(imageUrl).pathname);
    } catch {
      return part;
    }

    if (!isImageUrl) return part;

    return (
      <React.Fragment key={`image-link-${index}`}>
        <a href={imageUrl} target="_blank" rel="noreferrer">
          <img src={imageUrl} alt="Imagem anexada" style={styles.supportMessageImage} />
        </a>
        {trailingPunctuation}
      </React.Fragment>
    );
  });
};

const getSampleSupportMessages = (ticket) => {
  const senderType = getTicketSenderRole(ticket?.TIPO_REMETENTE || ticket?.tipo_remetente || ticket?.REMETENTE || ticket?.sender || "");
  const userName = ticket?.REMETENTE_NOME || ticket?.NOME_UTILIZADOR || ticket?.UTILIZADOR_NOME || "Tomador";
  const ticketMessage = ticket?.MENSAGEM || ticket?.DESCRICAO || "Olá, não consigo aceder à minha área de cliente. Podem ajudar?";
  const supportReply = "Olá! Recebemos o seu pedido. Pode indicar-nos em que momento surge o erro?";

  if (senderType === "support") {
    return [
      { id: "submitted-message", sender: "support", name: "Apoio ao cliente", text: supportReply },
      { id: "sample-reply", sender: "user", name: userName, text: ticketMessage },
    ];
  }

  return [
    { id: "submitted-message", sender: "user", name: userName, text: ticketMessage },
    { id: "sample-reply", sender: "support", name: "Apoio ao cliente", text: supportReply },
  ];
};

const normalizeSupportMessages = (messages = [], ticket) => {
  const userName = ticket.REMETENTE_NOME || ticket.NOME_UTILIZADOR || ticket.UTILIZADOR_NOME || "Utilizador";

  return (Array.isArray(messages) ? messages : [])
    .map((message, index) => {
      const rawText = message?.CONTEUDO || message?.MENSAGEM || message?.text || message?.mensagem || message?.DESCRICAO || "";
      const senderValue = String(message?.TIPO_REMETENTE || message?.tipo_remetente || message?.REMETENTE || message?.sender || message?.TIPO || message?.tipo || "");
      const isSupport = getTicketSenderRole(senderValue) === "support";
      const text = String(rawText).trim();

      return {
        id: message?.STAMP || message?.id || `ticket-message-${index}`,
        sender: isSupport ? "support" : "user",
        name: isSupport
          ? "Apoio ao cliente"
          : message?.REMETENTE_NOME || message?.NOME_REMETENTE || message?.name || userName,
        text: text || "Mensagem sem conteúdo",
      };
    })
    .filter((message) => message.text && message.text !== "Mensagem sem conteúdo");
};

const statusOptions = [
  { value: "aberto", label: "Aberto", icon: MdSchedule },
  { value: "em andamento", label: "Em andamento", icon: MdSchedule },
  { value: "pendente", label: "Pendente", icon: MdSchedule },
  { value: "concluido", label: "Concluído", icon: MdCheckCircle },
  { value: "rejeitado", label: "Rejeitado", icon: MdSchedule },
];

function TicketDetails({ ticket, isMobile, onBack, onUpdated }) {
  const [status, setStatus] = useState(getStatus(ticket));
  const [observation, setObservation] = useState(getObservation(ticket));
  const [saving, setSaving] = useState(false);
  const [loadingAction, setLoadingAction] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [error, setError] = useState("");
  const [supportMessages, setSupportMessages] = useState(() => getSampleSupportMessages(ticket));
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [supportDraft, setSupportDraft] = useState("");
  const [users, setUsers] = useState([]);
  const [tomadores, setTomadores] = useState([]);
  const [usersCursor, setUsersCursor] = useState(null);
  const [tomadoresCursor, setTomadoresCursor] = useState(null);
  const [usersHasMore, setUsersHasMore] = useState(true);
  const [tomadoresHasMore, setTomadoresHasMore] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTomadores, setLoadingTomadores] = useState(false);
  const [managerStamp, setManagerStamp] = useState(ticket.GESTOR_STAMP || ticket.GESTOR_ID || "");
  const [tomadorStamp, setTomadorStamp] = useState(ticket.TOMADOR_STAMP || ticket.TOMADOR_ID || "");
  const managerMissing = !hasManager(ticket);
  const tomadorMissing = !hasTomador(ticket);
  const showContactLists = status === "concluido";
  const statusOption = statusOptions.find((option) => option.value === status) || {
    value: status,
    label: status,
    icon: MdSchedule,
  };
  const StatusIcon = statusOption.icon;

  useEffect(() => {
    setStatus(getStatus(ticket));
    setObservation(getObservation(ticket));
    setFeedback("");
    setError("");
    setSupportDraft("");
    setManagerStamp(ticket.GESTOR_STAMP || ticket.GESTOR_ID || "");
    setTomadorStamp(ticket.TOMADOR_STAMP || ticket.TOMADOR_ID || "");
  }, [ticket]);

  useEffect(() => {
    let active = true;

    const loadTicketMessages = async () => {
      setLoadingMessages(true);

      try {
        const response = await getTicketMensagens(getTicketId(ticket));
        if (!active) return;

        const messages = normalizeSupportMessages(response?.mensagens, ticket);
        setSupportMessages(messages.length ? messages : getSampleSupportMessages(ticket));
      } catch (loadError) {
        if (active) {
          setSupportMessages(getSampleSupportMessages(ticket));
        }
      } finally {
        if (active) {
          setLoadingMessages(false);
        }
      }
    };

    if (getTicketId(ticket)) {
      loadTicketMessages();
    }

    return () => { active = false; };
  }, [getTicketId(ticket)]);

  useEffect(() => {
    let active = true;

    const loadMissingContacts = async () => {
      try {
        setLoadingUsers(showContactLists && (managerMissing || tomadorMissing));
        setLoadingTomadores(showContactLists && tomadorMissing);
        setUsers([]);
        setTomadores([]);
        setUsersCursor(null);
        setTomadoresCursor(null);
        setUsersHasMore(showContactLists && (managerMissing || tomadorMissing));
        setTomadoresHasMore(showContactLists && tomadorMissing);

        const requests = [
          showContactLists && (managerMissing || tomadorMissing) ? getUtilizadoresContact(20) : Promise.resolve({ utilizadores: [] }),
          showContactLists && tomadorMissing ? getTomadoresContact(20) : Promise.resolve({ tomadores: [] }),
        ];
        const [usersResult, tomadoresResult] = await Promise.all(requests);
        if (!active) return;
        setUsers(usersResult?.utilizadores || []);
        setTomadores(tomadoresResult?.tomadores || []);
        setUsersHasMore(usersResult?.hasMore ?? false);
        setTomadoresHasMore(tomadoresResult?.hasMore ?? false);
        setUsersCursor(usersResult?.lastCursor || null);
        setTomadoresCursor(tomadoresResult?.nextCursor || null);
      } catch (requestError) {
        if (active) setError("Não foi possível carregar os contactos disponíveis.");
      } finally {
        if (active) {
          setLoadingUsers(false);
          setLoadingTomadores(false);
        }
      }
    };

    loadMissingContacts();
    return () => { active = false; };
  }, [getTicketId(ticket), managerMissing, tomadorMissing, showContactLists]);

  const loadMoreUsers = async () => {
    if (loadingUsers || !usersHasMore || !showContactLists || !(managerMissing || tomadorMissing)) return;
    setLoadingUsers(true);
    try {
      const result = await getUtilizadoresContact(20, usersCursor);
      const newUsers = result?.utilizadores || [];
      setUsers((currentUsers) => {
        const merged = [...currentUsers, ...newUsers];
        return Array.from(new Map(merged.map((user) => [user.STAMP, user])).values());
      });
      setUsersHasMore(result?.hasMore ?? false);
      setUsersCursor(result?.lastCursor || null);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadMoreTomadores = async () => {
    if (loadingTomadores || !tomadoresHasMore || !showContactLists || !tomadorMissing) return;
    setLoadingTomadores(true);
    try {
      const result = await getTomadoresContact(20, tomadoresCursor);
      const newTomadores = result?.tomadores || [];
      setTomadores((currentTomadores) => {
        const merged = [...currentTomadores, ...newTomadores];
        return Array.from(new Map(merged.map((tomador) => [tomador.STAMP, tomador])).values());
      });
      setTomadoresHasMore(result?.hasMore ?? false);
      setTomadoresCursor(result?.nextCursor || null);
    } finally {
      setLoadingTomadores(false);
    }
  };

  const refreshTicketMessages = async () => {
    setLoadingMessages(true);

    try {
      const response = await getTicketMensagens(getTicketId(ticket));
      const messages = normalizeSupportMessages(response?.mensagens, ticket);
      setSupportMessages(messages.length ? messages : getSampleSupportMessages(ticket));
    } catch (loadError) {
      setSupportMessages(getSampleSupportMessages(ticket));
    } finally {
      setLoadingMessages(false);
    }
  };

  const title = ticket.TITULO || ticket.title || ticket.NOME || ticket.name || `Ticket ${getTicketRef(ticket)}`;
  const description = ticket.DESCRICAO || ticket.description || "Sem descrição disponível.";
  const responsibleName = ticket.GESTOR_NOME || ticket.gestor || ticket.RESPONSAVEL || ticket.responsavel || "Responsável não definido";
  const userName = ticket.REMETENTE_NOME || ticket.NOME_UTILIZADOR || ticket.UTILIZADOR_NOME || ticket.TOMADOR_NOME || ticket.tomador || "Utilizador";
  const userOptions = users.map((user) => ({ value: user.STAMP, label: user.NOME || user.STAMP, image: user.FOTO, icon: MdPersonOutline }));
  const tomadorOptions = tomadores.map((tomador) => ({ value: tomador.STAMP, label: tomador.NOME || tomador.STAMP, image: tomador.FOTO, icon: MdGroup }));
  const formatOptionLabel = ({ label, image, icon: Icon = MdPersonOutline }) => (
    <div style={styles.optionLabel}>
      {image ? <img src={image} alt="" style={styles.optionImage} /> : <Icon size={18} color="#6b7280" />}
      <span>{label}</span>
    </div>
  );
  const selectStyles = styles.selectStyles;

  const handleSupportSubmit = (event) => {
    event.preventDefault();
    const text = supportDraft.trim();
    if (!text) return;

    setSupportMessages((currentMessages) => [
      ...currentMessages,
      {
        id: `support-${Date.now()}`,
        sender: "support",
        name: "Apoio ao cliente",
        text,
      },
    ]);
    setSupportDraft("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setFeedback("");
    setError("");

    try {
      const statusUpdate = await updateTicketStatus(getTicketId(ticket), status);
      const payload = {
        OBSERVACAO: observation,
        ...(showContactLists && managerMissing && managerStamp ? { GESTOR: managerStamp } : {}),
        ...(showContactLists && tomadorMissing && tomadorStamp ? { TOMADOR: tomadorStamp } : {}),
      };

      const updatedTicket = Object.keys(payload).length > 0
        ? await updateTicket(getTicketId(ticket), payload)
        : statusUpdate;

      await refreshTicketMessages();
      setFeedback("Ticket atualizado com sucesso.");
      onUpdated?.(updatedTicket || statusUpdate || { ...ticket, ESTADO: status, OBSERVACAO: observation });
    } catch (requestError) {
      setError(requestError.message || "Não foi possível atualizar o ticket.");
    } finally {
      setSaving(false);
    }
  };

  const handleStartTreatment = async () => {
    if (saving || status === "em andamento") return;

    const previousStatus = status;
    const nextStatus = "aberto";

    setStatus(nextStatus);
    setSaving(true);
    setLoadingAction("assume");
    setFeedback("");
    setError("");

    try {
      const updatedTicket = await updateTicketStatus(getTicketId(ticket), nextStatus);
      const nextTicket = { ...ticket, ...(updatedTicket || {}), ESTADO: nextStatus };
      await refreshTicketMessages();
      setFeedback("Ticket assumido.");
      onUpdated?.(nextTicket);
    } catch (requestError) {
      setStatus(previousStatus);
      setError(requestError.message || "Não foi possível assumir o ticket.");
    } finally {
      setSaving(false);
      setLoadingAction(null);
    }
  };

  const handleRejectTicket = async () => {
    if (saving || status !== "aberto") return;

    setSaving(true);
    setLoadingAction("reject");
    setFeedback("");
    setError("");

    try {
      const updatedTicket = await updateTicketStatus(getTicketId(ticket), "rejeitado");
      const rejectedTicket = { ...ticket, ...(updatedTicket || {}), ESTADO: "rejeitado" };
      setStatus("rejeitado");
      await refreshTicketMessages();
      setFeedback("Ticket rejeitado.");
      onUpdated?.(rejectedTicket);
    } catch (requestError) {
      setError(requestError.message || "Não foi possível rejeitar o ticket.");
    } finally {
      setSaving(false);
      setLoadingAction(null);
    }
  };

  const handleCompleteTicket = async () => {
    if (saving || status !== "aberto") return;

    setSaving(true);
    setLoadingAction("complete");
    setFeedback("");
    setError("");

    try {
      const updatedTicket = await updateTicketStatus(getTicketId(ticket), "concluido");
      const completedTicket = { ...ticket, ...(updatedTicket || {}), ESTADO: "concluido" };
      setStatus("concluido");
      await refreshTicketMessages();
      setFeedback("Ticket concluído.");
      onUpdated?.(completedTicket);
    } catch (requestError) {
      setError(requestError.message || "Não foi possível concluir o ticket.");
    } finally {
      setSaving(false);
      setLoadingAction(null);
    }
  };

  return (
    <main style={styles.page(isMobile)}>
      <style>{`
        @keyframes ticketMessagesSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>

        <header style={styles.headerRow(isMobile)}>
          <button type="button" onClick={onBack} style={styles.backButton} aria-label="Voltar aos tickets">
            <MdArrowBack size={22} />
          </button>
          <div style={styles.iconBadge}><TfiTicket size={22} /></div>
          <div style={styles.headerText}>
            <h1 style={styles.title}>{title}</h1>
            <p style={styles.subtitle}>Detalhes do ticket {getTicketRef(ticket)}</p>
          </div>
      </header>
      <section style={styles.card(isMobile)}>
        <div style={styles.sectionHeader}>
          <div>
            <h2 style={styles.cardTitle}>Informação do ticket</h2>
            <p style={styles.cardDescription}>{description}</p>
          </div>
          <div style={styles.statusReadOnly(status)} aria-label={`Estado: ${statusOption.label}`}>
            <StatusIcon size={16} />
            <span>{statusOption.label}</span>
          </div>
        </div>

       

        <section style={styles.supportSection} aria-label="Conversa de suporte demonstrativa">
          <div style={styles.supportHeader}>
            <div>
              <h2 style={styles.cardTitle}>Conversa de suporte</h2>
              <p style={styles.supportDescription}>Mensagem enviada pelo tomador e resposta automática enviada.</p>
            </div>
           </div>

          {loadingMessages ? (
            <div style={styles.loadingMessagesContainer} aria-live="polite" aria-busy="true">
              <div style={styles.loadingMessagesSpinner} />
              <span style={styles.loadingMessagesText}>A carregar mensagens...</span>
            </div>
          ) : (
            <div style={styles.supportMessages} aria-live="polite">
              {supportMessages.map((message) => (
                <div key={message.id} style={styles.supportMessageRow(message.sender)}>
                  <div style={styles.supportBubble(message.sender)}>
                    <strong style={styles.supportSender}>{message.name}</strong>
                    <p style={styles.supportMessageText}>{renderSupportMessage(message.text)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {status === "aberto" && (
            <>
              <div style={styles.responsibleGrid}>
                <div style={styles.responsibleCard}>
                  <span style={styles.responsibleLabel}>Responsável</span>
                  <strong style={styles.responsibleValue}>{responsibleName}</strong>
                </div>
                <div style={styles.responsibleCard}>
                  <span style={styles.responsibleLabel}>Tomador</span>
                  <strong style={styles.responsibleValue}>{userName}</strong>
                </div>
              </div>

              <div style={styles.supportComposer}>
                <textarea
                  value={supportDraft}
                  onChange={(event) => setSupportDraft(event.target.value)}
                  placeholder="Responder ao tomador..."
                  style={styles.supportInput}
                  rows={3}
                />
                <button type="button" onClick={handleSupportSubmit} disabled={!supportDraft.trim()} style={styles.supportSendButton} aria-label="Enviar resposta ao tomador">
                  <MdSend size={18} />
                </button>
              </div>

              <p style={styles.supportNote}>As mensagens e respostas ficam guardadas no histórico do ticket.</p>
            </>
          )}
        </section>


         {!hasManager(ticket) && status !== "em andamento" && status !== "concluido" && status !== "rejeitado" && (
          <button type="button" onClick={handleStartTreatment} disabled={saving} aria-busy={loadingAction === "assume"} style={styles.startButton}>
            {loadingAction === "assume" ? <span style={styles.assumingSpinner} aria-hidden="true" /> : <MdPlayArrow size={19} />}
            <span>{loadingAction === "assume" ? "A assumir..." : "Assumir ticket"}</span>
          </button>
        )}

        {status === "aberto" && (
          <>
            <button type="button" onClick={handleRejectTicket} disabled={saving} aria-busy={loadingAction === "reject"} style={{ ...styles.startButton, ...styles.rejectButton }}>
              {loadingAction === "reject" ? <span style={styles.assumingSpinner} aria-hidden="true" /> : <MdCancel size={19} />}
              <span>{loadingAction === "reject" ? "A rejeitar..." : "Rejeitar"}</span>
            </button>
            <button type="button" onClick={handleCompleteTicket} disabled={saving} aria-busy={loadingAction === "complete"} style={{ ...styles.startButton, ...styles.completeButton }}>
              {loadingAction === "complete" ? <span style={styles.assumingSpinner} aria-hidden="true" /> : <MdCheckCircle size={19} />}
              <span>{loadingAction === "complete" ? "A concluir..." : "Concluir"}</span>
            </button>
          </>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          {showContactLists && managerMissing && (
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="ticket-manager">Gestor</label>
              <Select
                inputId="ticket-manager"
                options={userOptions}
                value={userOptions.find((option) => String(option.value) === String(managerStamp)) || null}
                onChange={(option) => setManagerStamp(option?.value || "")}
                placeholder="Selecionar gestor"
                formatOptionLabel={formatOptionLabel}
                isSearchable
                isLoading={loadingUsers}
                onMenuScrollToBottom={loadMoreUsers}
                styles={selectStyles}
              />
            </div>
          )}

          {showContactLists && tomadorMissing && (
            <>
              <div style={styles.formGroup}>
                <label style={styles.label} htmlFor="ticket-tomador">Tomador</label>
                <Select
                  inputId="ticket-tomador"
                  options={tomadorOptions}
                  value={tomadorOptions.find((option) => String(option.value) === String(tomadorStamp)) || null}
                  onChange={(option) => setTomadorStamp(option?.value || "")}
                  placeholder="Selecionar tomador"
                  formatOptionLabel={formatOptionLabel}
                  isSearchable
                  isLoading={loadingTomadores}
                  onMenuScrollToBottom={loadMoreTomadores}
                  styles={selectStyles}
                />
              </div>
            </>
          )}



          {error && <div role="alert" style={styles.error}>{error}</div>}
          {feedback && <div role="status" style={styles.success}><MdCheckCircle size={18} />{feedback}</div>}

        </form>
      </section>
    </main>
  );
}

export default TicketDetails;
